import { useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDataProvider, useNotify, type Identifier } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import { uploadAdminSubscriptionPlanImage } from '../admin/adminSubscriptionDataProvider';

import {
    BILLING_PLANS_RESOURCE,
    type BillingCountryCurrency,
    type BillingPlan,
} from './billingData';
import {
    formatCurrency,
    formatUpdatedAt,
    getPlanPriceCount,
    getPlanPriceSummary,
    getPlanPrices,
    parseNumber,
} from './billingUtils';
import { convertAndRoundCurrencyAmount, getLatestFrankfurterRates } from './frankfurterRates';
import { getPurchasingPowerHints, type PurchasingPowerHint } from './purchasingPowerHints';
import { applyFamiliarPricePoint } from './localizedPricing';

const MAX_PLAN_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

type PlanDraft = {
    id?: Identifier;
    name: string;
    description: string;
    imageUrl: string;
    imageFile: File | null;
    durationDays: number;
    isActive: boolean;
    prices: PlanPriceDraft[];
};

type PlanPriceDraft = {
    countryCode: string;
    countryName: string;
    currency: string;
    priceAmountInput: string;
    isActive: boolean;
};

type BillingPlansSectionProps = {
    countryCurrencies: BillingCountryCurrency[];
    plans: BillingPlan[];
    onChanged: () => Promise<void>;
};

type PlanStatusFilter = 'active' | 'inactive' | 'all';

function createPlanPriceDrafts(
    countryCurrencies: BillingCountryCurrency[],
    existingPlan?: BillingPlan,
): PlanPriceDraft[] {
    const existingPrices = new Map(
        (existingPlan ? getPlanPrices(existingPlan) : []).map((price) => [price.countryCode, price]),
    );

    return countryCurrencies.map((countryCurrency) => {
        const existingPrice = existingPrices.get(countryCurrency.countryCode);

        return {
            countryCode: countryCurrency.countryCode,
            countryName: countryCurrency.countryName,
            currency: countryCurrency.currency,
            priceAmountInput:
                existingPrice && Number.isFinite(existingPrice.priceAmount)
                    ? String(Math.round(existingPrice.priceAmount))
                    : '',
            isActive: existingPrice?.isActive ?? true,
        };
    });
}

function emptyPlanDraft(countryCurrencies: BillingCountryCurrency[]): PlanDraft {
    return {
        name: '',
        description: '',
        imageUrl: '',
        imageFile: null,
        durationDays: 30,
        isActive: true,
        prices: createPlanPriceDrafts(countryCurrencies),
    };
}

type GeneratedPriceBreakdown = {
    exchangeRatePrice: number;
    marketAdjustedPrice: number;
    finalPrice: number;
    inrEquivalentPrice?: number;
    inrPerCurrencyUnit?: number;
};

export function BillingPlansSection({ countryCurrencies, plans, onChanged }: BillingPlansSectionProps) {
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [planDraft, setPlanDraft] = useState<PlanDraft>(emptyPlanDraft(countryCurrencies));
    const [pendingDeleteId, setPendingDeleteId] = useState<Identifier | null>(null);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [statusFilter, setStatusFilter] = useState<PlanStatusFilter>('active');
    const [exchangeRateBaseCountryCode, setExchangeRateBaseCountryCode] = useState('');
    const [benchmarkPriceInput, setBenchmarkPriceInput] = useState('');
    const [isLoadingExchangeRates, setIsLoadingExchangeRates] = useState(false);
    const [exchangeRateSummary, setExchangeRateSummary] = useState('');
    const [purchasingPowerHints, setPurchasingPowerHints] = useState<Record<string, PurchasingPowerHint>>({});
    const [isLoadingPurchasingPowerHints, setIsLoadingPurchasingPowerHints] = useState(false);
    const [generatedPriceBreakdowns, setGeneratedPriceBreakdowns] = useState<Record<string, GeneratedPriceBreakdown>>({});
    const [inrPerCurrencyUnit, setInrPerCurrencyUnit] = useState<Record<string, number>>({ INR: 1 });
    const [isLoadingInrEquivalents, setIsLoadingInrEquivalents] = useState(false);

    const activePlanCount = useMemo(() => plans.filter((plan) => plan.isActive).length, [plans]);
    const inactivePlanCount = plans.length - activePlanCount;
    const filteredPlans = useMemo(
        () =>
            plans.filter((plan) => {
                if (statusFilter === 'active') return plan.isActive;
                if (statusFilter === 'inactive') return !plan.isActive;
                return true;
            }),
        [plans, statusFilter],
    );

    const loadPurchasingPowerHints = async (prices: PlanPriceDraft[]) => {
        setPurchasingPowerHints({});
        setIsLoadingPurchasingPowerHints(true);
        try {
            const hints = await getPurchasingPowerHints(prices.map((price) => price.countryCode));
            setPurchasingPowerHints(hints);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load country capacity hints';
            notify(`Country purchasing-capacity hints are unavailable: ${message}`, { type: 'warning' });
        } finally {
            setIsLoadingPurchasingPowerHints(false);
        }
    };

    const loadInrConversionRates = async (prices: PlanPriceDraft[]) => {
        setInrPerCurrencyUnit({ INR: 1 });
        setIsLoadingInrEquivalents(true);
        try {
            const result = await getLatestFrankfurterRates(
                'INR',
                prices.map((price) => price.currency),
            );
            const nextRates: Record<string, number> = { INR: 1 };

            prices.forEach((price) => {
                const inrToCurrencyRate = result.rates.get(price.currency);
                if (inrToCurrencyRate !== undefined) {
                    nextRates[price.currency] = 1 / inrToCurrencyRate;
                }
            });

            setInrPerCurrencyUnit(nextRates);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load INR conversion rates';
            notify(`INR equivalents are unavailable: ${message}`, { type: 'warning' });
        } finally {
            setIsLoadingInrEquivalents(false);
        }
    };

    const openCreatePlanDialog = () => {
        const nextDraft = emptyPlanDraft(countryCurrencies);
        const benchmarkPrice =
            nextDraft.prices.find((price) => price.countryCode === 'IN') ??
            nextDraft.prices.find((price) => price.currency === 'USD') ??
            nextDraft.prices[0];
        setPlanDraft(nextDraft);
        setExchangeRateBaseCountryCode(benchmarkPrice?.countryCode ?? '');
        setBenchmarkPriceInput(benchmarkPrice?.priceAmountInput ?? '');
        setExchangeRateSummary('');
        setGeneratedPriceBreakdowns({});
        setPlanDialogOpen(true);
        void loadPurchasingPowerHints(nextDraft.prices);
        void loadInrConversionRates(nextDraft.prices);
    };

    const openEditPlanDialog = (plan: BillingPlan) => {
        const nextDraft = {
            id: plan.id,
            name: plan.name,
            description: plan.description,
            imageUrl: plan.imageUrl,
            imageFile: null,
            durationDays: plan.durationDays,
            isActive: plan.isActive,
            prices: createPlanPriceDrafts(countryCurrencies, plan),
        };
        setPlanDraft(nextDraft);
        const pricedMarkets = nextDraft.prices.filter((price) => price.priceAmountInput.trim());
        const benchmarkPrice =
            pricedMarkets.find((price) => price.countryCode === 'IN') ??
                pricedMarkets.find((price) => price.currency === 'USD') ??
                pricedMarkets[0] ??
                nextDraft.prices.find((price) => price.countryCode === 'IN') ??
                nextDraft.prices.find((price) => price.currency === 'USD') ??
                nextDraft.prices[0];
        setExchangeRateBaseCountryCode(benchmarkPrice?.countryCode ?? '');
        setBenchmarkPriceInput(benchmarkPrice?.priceAmountInput ?? '');
        setExchangeRateSummary('');
        setGeneratedPriceBreakdowns({});
        setPlanDialogOpen(true);
        void loadPurchasingPowerHints(nextDraft.prices);
        void loadInrConversionRates(nextDraft.prices);
    };

    const updatePriceRow = (index: number, nextPrice: PlanPriceDraft) => {
        setPlanDraft((current) => ({
            ...current,
            prices: current.prices.map((price, itemIndex) => (itemIndex === index ? nextPrice : price)),
        }));
    };

    const handlePlanImageSelection = (file: File | null) => {
        if (!file) {
            setPlanDraft((current) => ({ ...current, imageFile: null }));
            return;
        }

        if (!file.type.startsWith('image/')) {
            notify('Only image files are allowed for the plan image', { type: 'warning' });
            return;
        }

        if (file.size > MAX_PLAN_IMAGE_SIZE_BYTES) {
            notify('Plan image must be 5 MB or smaller', { type: 'warning' });
            return;
        }

        setPlanDraft((current) => ({ ...current, imageFile: file }));
    };

    const handleGenerateCountryPrices = async (targetCountryCode?: string) => {
        if (isLoadingExchangeRates) return;

        const basePrice = planDraft.prices.find(
            (price) => price.countryCode === exchangeRateBaseCountryCode,
        );
        const baseAmount = Number(benchmarkPriceInput);

        if (!basePrice || !benchmarkPriceInput.trim() || !Number.isFinite(baseAmount) || baseAmount <= 0) {
            notify('Enter a benchmark price greater than zero first', { type: 'warning' });
            return;
        }

        const baseMarketHint = purchasingPowerHints[basePrice.countryCode];
        if (!baseMarketHint) {
            notify('Wait for market segmentation to finish loading before generating prices', { type: 'warning' });
            return;
        }

        setIsLoadingExchangeRates(true);
        setExchangeRateSummary('');
        try {
            const result = await getLatestFrankfurterRates(
                basePrice.currency,
                [...planDraft.prices.map((price) => price.currency), 'INR'],
            );
            const inrRate = result.rates.get('INR');
            const missingCurrencies = new Set<string>();
            let updatedCountryCount = 0;
            let preservedCountryCount = 0;
            const nextBreakdowns: Record<string, GeneratedPriceBreakdown> = {};
            const nextPrices = planDraft.prices.map((price) => {
                if (targetCountryCode && price.countryCode !== targetCountryCode) {
                    return price;
                }

                if (!targetCountryCode && price.priceAmountInput.trim()) {
                    preservedCountryCount += 1;
                    return price;
                }

                const rate = result.rates.get(price.currency);
                const marketHint = purchasingPowerHints[price.countryCode];
                if (rate === undefined || !marketHint) {
                    missingCurrencies.add(price.currency);
                    return price;
                }

                updatedCountryCount += 1;
                const exchangeRatePrice = Number(convertAndRoundCurrencyAmount(baseAmount, rate));
                const marketAdjustedPrice =
                    baseAmount * (marketHint.pricingMultiplier / baseMarketHint.pricingMultiplier) * rate;
                const finalPrice = applyFamiliarPricePoint(marketAdjustedPrice, price.currency);
                const inrPerCurrencyUnit = inrRate === undefined ? undefined : inrRate / rate;
                nextBreakdowns[price.countryCode] = {
                    exchangeRatePrice,
                    marketAdjustedPrice,
                    finalPrice,
                    inrEquivalentPrice:
                        inrPerCurrencyUnit === undefined
                            ? undefined
                            : Math.round(finalPrice * inrPerCurrencyUnit),
                    inrPerCurrencyUnit,
                };
                return {
                    ...price,
                    priceAmountInput: String(finalPrice),
                };
            });

            if (inrRate !== undefined) {
                const nextInrRates: Record<string, number> = { INR: 1 };
                planDraft.prices.forEach((price) => {
                    const currencyRate = result.rates.get(price.currency);
                    if (currencyRate !== undefined) {
                        nextInrRates[price.currency] = inrRate / currencyRate;
                    }
                });
                setInrPerCurrencyUnit(nextInrRates);
            }

            setPlanDraft((current) => ({ ...current, prices: nextPrices }));
            setGeneratedPriceBreakdowns(nextBreakdowns);

            const dateText = result.rateDates.length > 0
                ? ` Rate date${result.rateDates.length === 1 ? '' : 's'}: ${result.rateDates.join(', ')}.`
                : '';
            setExchangeRateSummary(
                `Generated ${updatedCountryCount} localized countr${updatedCountryCount === 1 ? 'y price' : 'y prices'} from ${basePrice.countryName} (${basePrice.currency}), including market weighting and familiar price rounding.${preservedCountryCount > 0 ? ` Preserved ${preservedCountryCount} existing ${preservedCountryCount === 1 ? 'price' : 'prices'}.` : ''}${dateText}`,
            );

            if (missingCurrencies.size > 0) {
                notify(
                    `No Frankfurter rate was available for ${Array.from(missingCurrencies).sort().join(', ')}; those prices were left unchanged.`,
                    { type: 'warning' },
                );
            } else {
                notify(
                    targetCountryCode
                        ? 'Generated the localized country price'
                        : `Generated ${updatedCountryCount} missing localized ${updatedCountryCount === 1 ? 'price' : 'prices'}; existing prices were preserved`,
                    { type: 'info' },
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load exchange rates';
            notify(`Unable to set country prices: ${message}`, { type: 'error' });
        } finally {
            setIsLoadingExchangeRates(false);
        }
    };

    const handleDelete = async (plan: BillingPlan) => {
        if (pendingDeleteId !== null) return;

        setPendingDeleteId(plan.id);
        try {
            await dataProvider.delete(BILLING_PLANS_RESOURCE, { id: plan.id, previousData: plan });
            notify('Plan deleted', { type: 'info' });
            await onChanged();
        } catch (error) {
            const message =
                error instanceof Error && error.message.includes('PLAN_IN_USE')
                    ? 'This plan is already used by subscriptions or payments, so it cannot be deleted.'
                    : error instanceof Error
                      ? error.message
                      : 'Delete failed';
            notify(message, { type: 'error' });
        } finally {
            setPendingDeleteId(null);
        }
    };

    const handleSavePlan = async () => {
        if (isSavingPlan) return;

        const prices = planDraft.prices
            .map((price) => ({
                countryCode: price.countryCode.trim().toUpperCase(),
                priceAmount: Math.round(parseNumber(price.priceAmountInput, 0)),
                isActive: price.isActive,
            }))
            .filter((price, index) => price.countryCode && planDraft.prices[index].priceAmountInput.trim() !== '');

        const uniqueCountryCount = new Set(prices.map((price) => price.countryCode)).size;

        if (!planDraft.name.trim()) {
            notify('Plan name is required', { type: 'warning' });
            return;
        }

        if (prices.length === 0) {
            notify('Add at least one market price for the plan', { type: 'warning' });
            return;
        }

        if (uniqueCountryCount !== prices.length) {
            notify('Each country can only appear once in a plan price list', { type: 'warning' });
            return;
        }

        const payload = {
            name: planDraft.name.trim(),
            description: planDraft.description.trim(),
            durationDays: planDraft.durationDays,
            isActive: planDraft.isActive,
            prices,
        };

        setIsSavingPlan(true);
        try {
            let result;
            if (planDraft.id) {
                result = await dataProvider.update(BILLING_PLANS_RESOURCE, {
                    id: planDraft.id,
                    data: payload,
                    previousData: plans.find((plan) => plan.id === planDraft.id),
                });
            } else {
                result = await dataProvider.create(BILLING_PLANS_RESOURCE, { data: payload });
            }

            const savedPlanId = result?.data?.id ?? planDraft.id;

            if (planDraft.imageFile && savedPlanId) {
                try {
                    await uploadAdminSubscriptionPlanImage(savedPlanId, planDraft.imageFile);
                } catch (error) {
                    const message =
                        error instanceof Error
                            ? error.message
                            : 'Plan saved, but the image upload failed';
                    notify(`Plan saved, but the image upload failed: ${message}`, { type: 'warning' });
                }
            }

            notify(planDraft.id ? 'Plan updated' : 'Plan created', { type: 'info' });
            setPlanDialogOpen(false);
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save plan';
            notify(message, { type: 'error' });
        } finally {
            setIsSavingPlan(false);
        }
    };

    return (
        <>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Plans only show Airwallex-enabled markets. Leave a market price blank if that enabled
                        country should not be priced yet.
                    </Typography>
                    <Button variant="contained" onClick={openCreatePlanDialog} disabled={isSavingPlan || pendingDeleteId !== null}>
                        Add Plan
                    </Button>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        size="small"
                        onChange={(_event, nextValue: PlanStatusFilter | null) => {
                            if (nextValue) {
                                setStatusFilter(nextValue);
                            }
                        }}
                    >
                        <ToggleButton value="active">Active ({activePlanCount})</ToggleButton>
                        <ToggleButton value="inactive">Inactive ({inactivePlanCount})</ToggleButton>
                        <ToggleButton value="all">All ({plans.length})</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                        Showing {filteredPlans.length} plan{filteredPlans.length === 1 ? '' : 's'}
                    </Typography>
                </Stack>
                <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                    {filteredPlans.map((plan) => (
                        <Card key={plan.id} variant="outlined">
                            <CardContent>
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Avatar
                                            src={plan.imageUrl || undefined}
                                            alt={plan.name}
                                            variant="rounded"
                                            sx={{ width: 48, height: 48 }}
                                        >
                                            {plan.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                                {plan.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {plan.description || 'No description'}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <Stack spacing={0.75}>
                                        <Typography variant="body2">Duration: {plan.durationDays} days</Typography>
                                        <Typography variant="body2">
                                            Regional Prices: {getPlanPriceCount(plan, countryCurrencies)} market price
                                            {getPlanPriceCount(plan, countryCurrencies) === 1 ? '' : 's'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {getPlanPriceSummary(plan, countryCurrencies)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Updated: {formatUpdatedAt(plan.updatedAt)}
                                        </Typography>
                                    </Stack>

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    >
                                        <Chip
                                            label={plan.isActive ? 'Status: Active' : 'Status: Inactive'}
                                            color={plan.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                        <Button variant="outlined" size="small" onClick={() => openEditPlanDialog(plan)} disabled={pendingDeleteId !== null}>
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={() => void handleDelete(plan)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            {pendingDeleteId === plan.id ? 'Deleting...' : 'Delete'}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {filteredPlans.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                No {statusFilter === 'all' ? '' : `${statusFilter} `}plans to show.
                            </Typography>
                        </Paper>
                    ) : null}
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Regional Prices</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Updated</TableCell>
                                <DesktopActionHeaderCell>Edit</DesktopActionHeaderCell>
                                <DesktopActionHeaderCell>Delete</DesktopActionHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPlans.map((plan) => (
                                <TableRow key={plan.id} hover>
                                    <TableCell>
                                        <Avatar
                                            src={plan.imageUrl || undefined}
                                            alt={plan.name}
                                            variant="rounded"
                                            sx={{ width: 48, height: 48 }}
                                        >
                                            {plan.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>{plan.name}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 260 }}>
                                            {plan.description || 'No description'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{plan.durationDays} days</TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body2">
                                                {getPlanPriceCount(plan, countryCurrencies)} market price
                                                {getPlanPriceCount(plan, countryCurrencies) === 1 ? '' : 's'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {getPlanPriceSummary(plan, countryCurrencies)}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={plan.isActive ? 'Active' : 'Inactive'}
                                            color={plan.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatUpdatedAt(plan.updatedAt)}</TableCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Edit plan"
                                            onClick={() => openEditPlanDialog(plan)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Delete plan"
                                            onClick={() => void handleDelete(plan)}
                                            disabled={pendingDeleteId !== null}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                </TableRow>
                            ))}
                            {filteredPlans.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        <Typography variant="body2" color="text.secondary">
                                            No {statusFilter === 'all' ? '' : `${statusFilter} `}plans to show.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>

            <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} fullWidth maxWidth="xl">
                <DialogTitle>{planDraft.id ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Name"
                            value={planDraft.name}
                            onChange={(event) =>
                                setPlanDraft((current) => ({ ...current, name: event.target.value }))
                            }
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={planDraft.description}
                            onChange={(event) =>
                                setPlanDraft((current) => ({ ...current, description: event.target.value }))
                            }
                            multiline
                            minRows={3}
                            fullWidth
                        />
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2">Plan Image</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                <Avatar
                                    src={planDraft.imageUrl || undefined}
                                    alt={planDraft.name || 'Plan image'}
                                    variant="rounded"
                                    sx={{ width: 72, height: 72 }}
                                >
                                    {(planDraft.name || 'P').charAt(0).toUpperCase()}
                                </Avatar>
                                <Stack spacing={1} sx={{ flex: 1 }}>
                                    <Button component="label" variant="outlined">
                                        {planDraft.imageFile ? 'Replace Image' : 'Upload Image'}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={(event) => {
                                                const file = event.target.files?.[0] ?? null;
                                                handlePlanImageSelection(file);
                                                event.target.value = '';
                                            }}
                                        />
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        {planDraft.imageFile
                                            ? `Selected file: ${planDraft.imageFile.name}`
                                            : planDraft.imageUrl
                                              ? 'Current uploaded image will be kept until you choose a new file.'
                                              : 'No image uploaded yet.'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Accepted: image files up to 5 MB.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                        <TextField
                            label="Duration Days"
                            type="number"
                            value={planDraft.durationDays}
                            onChange={(event) =>
                                setPlanDraft((current) => ({
                                    ...current,
                                    durationDays: parseNumber(event.target.value, 30),
                                }))
                            }
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={planDraft.isActive}
                                    onChange={(event) =>
                                        setPlanDraft((current) => ({
                                            ...current,
                                            isActive: event.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Plan is active"
                        />

                        <Stack spacing={0.5}>
                            <Typography variant="h6">Localized market pricing</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Generate final whole-number prices using live currency conversion, market-tier weighting,
                                and familiar price points. India is the preferred value-market benchmark when available.
                            </Typography>
                        </Stack>

                        {planDraft.prices.length > 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1">Pricing generator</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Select the benchmark market, enter its benchmark price below, then generate every
                                        final country price. For example, an India benchmark of 500 becomes 499;
                                        Australia is weighted at 1.30x and the US at 1.80x before local-currency conversion
                                        and familiar-price rounding.
                                    </Typography>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1.5}
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                    >
                                        <TextField
                                            select
                                            label="Benchmark market"
                                            value={exchangeRateBaseCountryCode}
                                            onChange={(event) => {
                                                setExchangeRateBaseCountryCode(event.target.value);
                                                setBenchmarkPriceInput(
                                                    planDraft.prices.find(
                                                        (price) => price.countryCode === event.target.value,
                                                    )?.priceAmountInput ?? '',
                                                );
                                                setGeneratedPriceBreakdowns({});
                                                setExchangeRateSummary('');
                                            }}
                                            fullWidth
                                        >
                                            {planDraft.prices.map((price) => (
                                                <MenuItem key={price.countryCode} value={price.countryCode}>
                                                    {price.countryName} ({price.countryCode}) - {price.currency}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            label={`Benchmark price (${planDraft.prices.find((price) => price.countryCode === exchangeRateBaseCountryCode)?.currency ?? 'INR'})`}
                                            type="text"
                                            value={benchmarkPriceInput}
                                            onChange={(event) => {
                                                if (/^\d*$/.test(event.target.value)) {
                                                    setBenchmarkPriceInput(event.target.value);
                                                    setGeneratedPriceBreakdowns({});
                                                    setExchangeRateSummary('');
                                                }
                                            }}
                                            placeholder="Example: 500"
                                            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
                                            fullWidth
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={() => void handleGenerateCountryPrices()}
                                            disabled={
                                                isLoadingExchangeRates ||
                                                isLoadingPurchasingPowerHints ||
                                                !exchangeRateBaseCountryCode
                                            }
                                            sx={{ minWidth: { md: 220 } }}
                                        >
                                            {isLoadingExchangeRates ? 'Generating prices...' : 'Generate final prices'}
                                        </Button>
                                    </Stack>
                                    {exchangeRateSummary ? <Alert severity="success">{exchangeRateSummary}</Alert> : null}
                                    <Typography variant="caption" color="text.secondary">
                                        Generate final prices fills blank price fields only and preserves existing values.
                                        Use a country&apos;s Generate button to recalculate that country individually.
                                    </Typography>
                                </Stack>
                            </Paper>
                        ) : null}

                        {planDraft.prices.map((price, index) => {
                            const capacityHint = purchasingPowerHints[price.countryCode];
                            const generatedBreakdown = generatedPriceBreakdowns[price.countryCode];
                            const finalPrice = Number(price.priceAmountInput);
                            const currencyToInrRate = inrPerCurrencyUnit[price.currency];
                            const inrEquivalentPrice =
                                price.priceAmountInput.trim() &&
                                Number.isFinite(finalPrice) &&
                                currencyToInrRate !== undefined
                                    ? Math.round(finalPrice * currencyToInrRate)
                                    : undefined;

                            return (
                                <Paper key={`${price.countryCode}-${index}`} variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={1.5}>
                                        <Stack
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                    xs: '1fr',
                                                    md: 'minmax(220px, 1.2fr) minmax(210px, 1fr) minmax(240px, 1.2fr) auto auto',
                                                },
                                                gap: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Stack spacing={0.25}>
                                                <Typography variant="subtitle2">{price.countryName}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {price.countryCode} · {price.currency}
                                                </Typography>
                                            </Stack>
                                            {isLoadingPurchasingPowerHints ? (
                                                <Chip label="Classifying market..." size="small" variant="outlined" />
                                            ) : (
                                                <Tooltip
                                                    title={capacityHint?.detail ?? 'No market classification is available.'}
                                                    arrow
                                                >
                                                    <Chip
                                                        label={
                                                            capacityHint
                                                                ? `${capacityHint.label} · ${capacityHint.pricingMultiplier.toFixed(2)}x`
                                                                : 'Standard Market · 1.00x'
                                                        }
                                                        color={capacityHint?.color ?? 'default'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ justifySelf: { md: 'start' } }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Stack spacing={0.5}>
                                                <TextField
                                                    label={`Final price (${price.currency})`}
                                                    type="text"
                                                    value={price.priceAmountInput}
                                                    onChange={(event) => {
                                                        const nextValue = event.target.value;
                                                        if (/^\d*$/.test(nextValue)) {
                                                            updatePriceRow(index, {
                                                                ...price,
                                                                priceAmountInput: nextValue,
                                                            });
                                                            setGeneratedPriceBreakdowns((current) => {
                                                                const breakdown = current[price.countryCode];
                                                                if (!breakdown) return current;

                                                                const finalPrice = Number(nextValue);
                                                                return {
                                                                    ...current,
                                                                    [price.countryCode]: {
                                                                        ...breakdown,
                                                                        finalPrice:
                                                                            nextValue && Number.isFinite(finalPrice)
                                                                                ? finalPrice
                                                                                : 0,
                                                                        inrEquivalentPrice:
                                                                            nextValue &&
                                                                            Number.isFinite(finalPrice) &&
                                                                            breakdown.inrPerCurrencyUnit !== undefined
                                                                                ? Math.round(
                                                                                      finalPrice *
                                                                                          breakdown.inrPerCurrencyUnit,
                                                                                  )
                                                                                : undefined,
                                                                    },
                                                                };
                                                            });
                                                        }
                                                    }}
                                                    placeholder="Whole-number final price"
                                                    slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
                                                    fullWidth
                                                />
                                                {generatedBreakdown ? (
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            FX {formatCurrency(generatedBreakdown.exchangeRatePrice, price.currency)} ·
                                                            weighted {formatCurrency(generatedBreakdown.marketAdjustedPrice, price.currency)} ·
                                                            final {formatCurrency(generatedBreakdown.finalPrice, price.currency)}
                                                        </Typography>
                                                    </Stack>
                                                ) : null}
                                                {inrEquivalentPrice !== undefined ? (
                                                    <Typography variant="caption" color="primary.main" fontWeight={600}>
                                                        Approx. INR equivalent: {formatCurrency(inrEquivalentPrice, 'INR')}
                                                    </Typography>
                                                ) : price.priceAmountInput.trim() && isLoadingInrEquivalents ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Calculating INR equivalent...
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => void handleGenerateCountryPrices(price.countryCode)}
                                                disabled={
                                                    isLoadingExchangeRates ||
                                                    isLoadingPurchasingPowerHints ||
                                                    !exchangeRateBaseCountryCode
                                                }
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                Generate
                                            </Button>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={price.isActive}
                                                        onChange={(event) =>
                                                            updatePriceRow(index, {
                                                                ...price,
                                                                isActive: event.target.checked,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Active"
                                                sx={{ minWidth: 100, m: 0 }}
                                            />
                                        </Stack>
                                        {!price.priceAmountInput.trim() ? (
                                            <Typography variant="caption" color="text.secondary">
                                                Leave blank only if this enabled market should not have a saved price.
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Paper>
                            );
                        })}

                        {planDraft.prices.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No Airwallex-enabled countries are available for plan pricing.
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlanDialogOpen(false)} disabled={isSavingPlan || isLoadingExchangeRates}>Cancel</Button>
                    <Button variant="contained" onClick={() => void handleSavePlan()} disabled={isSavingPlan || isLoadingExchangeRates}>
                        {isSavingPlan ? 'Saving...' : 'Save Plan'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
